import React, { useMemo, useEffect } from 'react';
import BaseMap from './BaseMap';
import { scaleLinear } from 'd3-scale';
import { Box } from '@mui/material';
import { formatPercentage } from '../utils/mapUtils';
import { fipsToStateName } from '../utils/fipsUtils';

const FoodAccessMap = ({ data }) => {
  // Debug on mount
  useEffect(() => {
    console.log('FoodAccessMap data received:', data?.length || 0, 'items');
    const validRates = data.map(d => d.foodAccessIndex).filter(r => r !== undefined && !isNaN(r));
    if (validRates.length > 0) {
      console.log('Food Access Index range:', 
        Math.min(...validRates),
        'to',
        Math.max(...validRates));
    }
    
    // Log some examples of counties with high food access issues
    const examples = data
      ?.filter(d => d.foodAccessIndex !== undefined && !isNaN(d.foodAccessIndex))
      ?.sort((a, b) => b.foodAccessIndex - a.foodAccessIndex)
      ?.slice(0, 5) || [];
    
    if (examples.length > 0) {
      console.log('Examples of areas with limited food access:', 
        examples.map(d => `${d.countyName}, ${d.stateCode} (${formatPercentage(d.foodAccessIndex)})`));
    }
  }, [data]);

  // Convert data to a consistent format with normalized FIPS codes
  const processedData = useMemo(() => {
    return data?.map(item => ({
      fips: String(item.fips).padStart(5, '0'),
      foodAccessIndex: item.foodAccessIndex,
      countyName: item.countyName,
      stateCode: item.stateCode
    })) || [];
  }, [data]);

  // Continuous color scale for food access rate with purple colors
  const colorScale = useMemo(() => 
    scaleLinear()
      .domain([0, 10, 20, 30, 50, 80])  // Food access percentages
      .range(['#EEEEEE', '#f2f0f7', '#dadaeb', '#bcbddc', '#9e9ac8', '#756bb1']), // Purple gradient
  []);

  // Create legend data
  const legendColors = useMemo(() => 
    ['#f2f0f7', '#dadaeb', '#bcbddc', '#9e9ac8', '#756bb1'],
  []);

  const legendValues = useMemo(() => [
    '0-20%',
    '20-40%',
    '40-60%',
    '60-80%',
    '80-100%'
  ], []);

  // Update getFillColor to use direct matching first, then fallback
  const getFillColor = (geo, directMatch = null) => {
    // If we already have a direct match from BaseMap's matching logic, use it
    if (directMatch) {
      return directMatch.foodAccessIndex !== undefined && !isNaN(directMatch.foodAccessIndex)
        ? colorScale(directMatch.foodAccessIndex)
        : '#EEEEEE';
    }
    
    // Otherwise, do the matching here
    const fips = String(geo.id).padStart(5, '0');
    const countyData = processedData.find(d => d.fips === fips);
    
    return countyData?.foodAccessIndex !== undefined && !isNaN(countyData.foodAccessIndex)
      ? colorScale(countyData.foodAccessIndex)
      : '#EEEEEE';
  };

  // Tooltip handler showing food access percentage
  const handleMouseMove = (event, geo, countyData) => {
    const countyName = geo.properties?.name || 'Unknown';
    const stateFips = geo.id ? String(geo.id).substring(0, 2) : '';
    const stateName = fipsToStateName[stateFips] || '';
    
    // Check if we have a valid food access index
    if (!countyData?.foodAccessIndex) return 'No data available';
    
    // Format the percentage correctly based on its magnitude
    const formattedRate = countyData.foodAccessIndex > 1 ? 
      `${Math.round(countyData.foodAccessIndex * 10) / 10}%` : 
      `${Math.round(countyData.foodAccessIndex * 1000) / 10}%`;
    
    return `
      <strong>${countyName} County${stateName ? `, ${stateName}` : ''}</strong><br/>
      Limited Food Access: ${formattedRate}
    `;
  };

  // Add data validation
  useEffect(() => {
    console.log('[FoodAccessMap] Processed data sample:', {
      inputFips: data?.[0]?.fips,
      processedFips: processedData?.[0]?.fips,
      geoFipsExample: "46121",
      match: processedData?.some(d => d.fips === "46121"),
      exampleFoodAccess: processedData?.find(d => d.fips === "46121")?.foodAccessIndex
    });
  }, [processedData]);

  return (
    <BaseMap
      data={data}
      getFillColor={getFillColor}
      onHover={(e, geo, countyData) => handleMouseMove(e, geo, countyData)}
      legendTitle="Limited Food Access"
      legendColors={legendColors}
      legendValues={legendValues}
    />
  );
};

export default FoodAccessMap; 